import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Card, Col, Form, Input, Row, Select, Space, Tooltip, theme } from 'antd';
import PopoverTip from '@components/common/PopoverTip';
import { CredibilityLegendContent } from '@/components/common/ExpertCredibilityBadge';
import { filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout } from '@utils/property';
import { expertLevels, credibilityList } from '@utils/constants';

const VISIBLE_FIELD_COUNT = 9;
const TOTAL_FIELD_COUNT = 12;

const ExpertFilterForm = ({ handleFilterFinish, filter, setFilter }) => {
    const dispatch = useDispatch();
    const { token } = theme.useToken();

    const [form] = Form.useForm();
    const [expanded, setExpanded] = useState(false);
    const buttonColLayout = computeButtonColLayout(expanded ? TOTAL_FIELD_COUNT : VISIBLE_FIELD_COUNT);

    const hasPhoneFlag = Form.useWatch('hasPhoneFlag', form);

    const onFinish = values => {
        const newValues = { ...values };
        // 切换无手机号时，清空手机号字段已填内容
        newValues.memMobile = values.hasPhoneFlag === 'n' ? undefined : values.memMobile;
        handleFilterFinish(newValues);
    };

    const onReset = () => {
        form.resetFields();
        setExpanded(false);
        if (setFilter) {
            dispatch(
                setFilter({
                    page: 1,
                    pageSize: filter?.pageSize,
                }),
            );
            return;
        }
        handleFilterFinish?.({});
    };

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue(filter || {});
        const hasExpandedField = filter?.credibility != null || filter?.keyWords != null || filter?.status != null || filter?.unitJobTitle != null;
        if (hasExpandedField) {
            setExpanded(true);
        }
    }, [filter, form]);

    useEffect(() => {
        if (hasPhoneFlag === 'n') {
            form.setFieldValue('memMobile', undefined);
        }
    }, [form, hasPhoneFlag]);

    return (
        <Card
            size="small"
            className="expert-filter-panel"
            style={{
                marginBottom: token.marginLG,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillAlter,
            }}
        >
            <Form {...filterFormItemLayout} onFinish={onFinish} form={form} autoComplete="off" requiredMark={false}>
                <Row gutter={[16, 16]}>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="姓名" name="memUsername" style={filterFormItemStyle}>
                            <Input allowClear placeholder="请输入姓名" />
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="专家 ID" name="expertId" style={filterFormItemStyle}>
                            <Input allowClear placeholder="请输入专家 ID" />
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="有无手机号" name="hasPhoneFlag" style={filterFormItemStyle}>
                            <Select allowClear placeholder="请选择有无手机号">
                                <Select.Option value="y">有手机号</Select.Option>
                                <Select.Option value="n">无手机号</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    {hasPhoneFlag !== 'n' && (
                        <Col {...filterFormGridLayout}>
                            <Form.Item label="手机号" name="memMobile" style={filterFormItemStyle}>
                                <Input allowClear placeholder="请输入手机号" />
                            </Form.Item>
                        </Col>
                    )}
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="级别" name="expertLevels" style={filterFormItemStyle}>
                            <Select mode="multiple" maxTagCount="responsive" allowClear options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择专家级别" />
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="性别" name="memGender" style={filterFormItemStyle}>
                            <Select allowClear placeholder="选择平台用户性别">
                                <Select.Option value={1}>男</Select.Option>
                                <Select.Option value={0}>女</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="单位" name="unitName" style={filterFormItemStyle}>
                            <Input allowClear placeholder="请输入单位名称" />
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="科室/部门" name="unitOffices" style={filterFormItemStyle}>
                            <Input allowClear placeholder="请输入科室/部门" />
                        </Form.Item>
                    </Col>
                    <Col {...filterFormGridLayout}>
                        <Form.Item label="职务" name="unitPost" style={filterFormItemStyle}>
                            <Input allowClear placeholder="请输入职务" />
                        </Form.Item>
                    </Col>

                    {expanded && (
                        <>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="职称" name="unitJobTitle" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="请输入职称" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="专家状态" name="status" style={filterFormItemStyle}>
                                    <Select allowClear placeholder="选择专家状态">
                                        <Select.Option value={0}>正常</Select.Option>
                                        <Select.Option value={1}>禁用</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    label={
                                        <>
                                            <span className="form-item-label-text">可信度</span>
                                            <PopoverTip content={<CredibilityLegendContent />}>
                                                <i className="bi bi-question-circle-fill popover-tip-icon" style={{ marginLeft: 3 }}></i>
                                            </PopoverTip>
                                        </>
                                    }
                                    name="credibility"
                                    style={filterFormItemStyle}
                                >
                                    <Select allowClear placeholder="请选择可信度">
                                        {credibilityList.map(item => (
                                            <Select.Option key={item.value} value={item.value}>
                                                {item.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item
                                    label={
                                        <>
                                            <span className="form-item-label-text">关键词</span>
                                            <Tooltip
                                                title={
                                                    <>
                                                        关键词查询信息来源：
                                                        <br />
                                                        专家简介、学术任职、研究成果、管理专长、医学专长、荣誉头衔
                                                    </>
                                                }
                                            >
                                                <span style={{ marginLeft: 3, color: '#bfbfbf' }}>
                                                    <i className="bi bi-question-circle-fill"></i>
                                                </span>
                                            </Tooltip>
                                        </>
                                    }
                                    name="keyWords"
                                    style={filterFormItemStyle}
                                >
                                    <Input allowClear placeholder="请输入关键词" />
                                </Form.Item>
                            </Col>
                        </>
                    )}

                    <Col {...buttonColLayout}>
                        <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                            <Space size="small">
                                <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                    检索
                                </Button>
                                <Button type="default" onClick={onReset}>
                                    重置
                                </Button>
                                <Button type="link" size="small" aria-expanded={expanded} onClick={() => setExpanded(prev => !prev)} style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                    {expanded ? (
                                        <>
                                            收起 <span className="bi bi-chevron-up" aria-hidden="true" />
                                        </>
                                    ) : (
                                        <>
                                            展开 <span className="bi bi-chevron-down" aria-hidden="true" />
                                        </>
                                    )}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};

export default ExpertFilterForm;
